/**
 * Account Form Hook
 * Manages account creation/update form state and API integration
 */

import { useState, useEffect } from 'react';
import { accountAPI, accountConfigAPI } from '@/src/lib/api/accounts';
import { branchesAPI, type Branch } from '@/src/lib/api/branches';
import type {
  CreateAccountRequest,
  AccountFormState,
  AccountFormErrors,
  AccountConfig,
} from '@/src/types/account';

interface UseAccountFormProps {
  accountId?: number; // For edit mode
  branchId: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const initialFormState: AccountFormState = {
  account_source: 'bank',
  account_name: '',
  account_name_th: '',
  account_name_en: '',
  verification_type: 'account-number',
  bank_code: '',
  account_number: '',
  promptpay_type: 'phone',
  promptpay_id: '',
  qr_code_url: '',
  shop_type: 'shopee',
  shop_code: '',
  shop_name: '',
  truemoney_id: '',
  is_active: true,
  purpose: '',
  notes: '',
  linked_businesses: [],
};

export function useAccountForm({ 
  accountId, 
  branchId, 
  onSuccess, 
  onError 
}: UseAccountFormProps) {
  const [formData, setFormData] = useState<AccountFormState>(initialFormState);
  const [errors, setErrors] = useState<AccountFormErrors>({});
  const [config, setConfig] = useState<AccountConfig | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);

  // Load configuration on mount
  useEffect(() => {
    loadConfig();
    loadBranches();
  }, []);

  // Load existing account if in edit mode
  useEffect(() => {
    if (accountId) {
      loadAccount(accountId);
    }
  }, [accountId]);

  /**
   * Load available branches from API
   */
  const loadBranches = async () => {
    try {
      setIsLoadingBranches(true);
      const branchData = await branchesAPI.getAll();
      setBranches(branchData);
    } catch (error) {
      console.error('Failed to load branches:', error);
      onError?.(error as Error);
    } finally {
      setIsLoadingBranches(false);
    }
  };

  /**
   * Load account configuration from API
   */
  const loadConfig = async () => {
    try {
      setIsLoadingConfig(true);
      const configData = await accountConfigAPI.getConfig();
      setConfig(configData);
    } catch (error) {
      console.error('Failed to load account config:', error);
      onError?.(error as Error);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  /**
   * Load existing account for editing
   */
  const loadAccount = async (id: number) => {
    try {
      setIsLoading(true);
      const account = await accountAPI.getById(id);
      
      // Map account data to form state
      setFormData({
        account_source: account.account_source,
        account_name: account.account_name || '',
        account_name_th: account.account_name_th || '',
        account_name_en: account.account_name_en || '',
        verification_type: account.verification_type || 'account-number',
        bank_code: account.bank_code || '',
        account_number: account.account_number || '',
        promptpay_type: account.promptpay_type || 'phone',
        promptpay_id: account.promptpay_id || '',
        qr_code_url: account.qr_code_url || '',
        shop_type: account.shop_type || 'shopee',
        shop_code: account.shop_code || '',
        shop_name: account.shop_name || '',
        truemoney_id: account.truemoney_id || '',
        is_active: account.is_active,
        purpose: account.purpose || '',
        notes: account.notes || '',
        linked_businesses: [],
      });
    } catch (error) {
      console.error('Failed to load account:', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update form field
   */
  const updateField = <K extends keyof AccountFormState>(
    field: K,
    value: AccountFormState[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Validate form based on account source
   */
  const validateForm = (): boolean => {
    const newErrors: AccountFormErrors = {};

    // Bank account validation
    if (formData.account_source === 'bank') {
      if (!formData.bank_code) {
        newErrors.bank_code = 'กรุณาเลือกธนาคาร';
      }
      if (!formData.account_number.trim()) {
        newErrors.account_number = 'กรุณากรอกเลขที่บัญชี';
      } else if (!/^\d{10,15}$/.test(formData.account_number)) {
        newErrors.account_number = 'เลขที่บัญชีต้องเป็นตัวเลข 10-15 หลัก';
      }
      
      // Validate based on verification type
      if (formData.verification_type === 'account-name' || formData.verification_type === 'both') {
        if (!formData.account_name_th?.trim()) {
          newErrors.account_name_th = 'กรุณากรอกชื่อบัญชีภาษาไทย';
        }
      }
    } else {
      // For other account types, require account_name
      if (!formData.account_name.trim()) {
        newErrors.account_name = 'กรุณากรอกชื่อบัญชี';
      }
    }

    // PromptPay validation
    if (formData.account_source === 'promptpay') {
      if (!formData.promptpay_type) {
        newErrors.promptpay_type = 'กรุณาเลือกประเภทพร้อมเพย์';
      }
      if (!formData.promptpay_id.trim()) {
        newErrors.promptpay_id = 'กรุณากรอกหมายเลขพร้อมเพย์';
      } else {
        // Validate based on type
        if (formData.promptpay_type === 'phone') {
          if (!/^0\d{9}$/.test(formData.promptpay_id)) {
            newErrors.promptpay_id = 'เบอร์โทรศัพท์ต้องขึ้นต้นด้วย 0 และมี 10 หลัก';
          }
        } else if (formData.promptpay_type === 'id-card' || formData.promptpay_type === 'tax-id') {
          if (!/^\d{13}$/.test(formData.promptpay_id)) {
            newErrors.promptpay_id = 'เลขบัตรประชาชน/ผู้เสียภาษีต้องมี 13 หลัก';
          }
        }
      }
    }

    // Shop validation
    if (formData.account_source === 'shop') {
      if (!formData.shop_type) {
        newErrors.shop_type = 'กรุณาเลือกประเภทร้านค้า';
      }
      if (!formData.shop_code.trim()) {
        newErrors.shop_code = 'กรุณากรอกรหัสร้าน';
      } else if (formData.shop_code.length < 3 || formData.shop_code.length > 50) {
        newErrors.shop_code = 'รหัสร้านต้องมีความยาว 3-50 ตัวอักษร';
      }
      if (!formData.shop_name.trim()) {
        newErrors.shop_name = 'กรุณากรอกชื่อร้าน';
      }
    }

    // TrueMoney validation
    if (formData.account_source === 'truemoney') {
      if (!formData.truemoney_id.trim()) {
        newErrors.truemoney_id = 'กรุณากรอกหมายเลขโทรศัพท์';
      } else if (!/^0\d{9}$/.test(formData.truemoney_id)) {
        newErrors.truemoney_id = 'หมายเลขโทรศัพท์ต้องขึ้นต้นด้วย 0 และมี 10 หลัก';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Build request data based on account source
   */
  const buildRequestData = (): CreateAccountRequest => {
    const baseData = {
      account_source: formData.account_source,
      account_name: formData.account_name,
      account_name_th: formData.account_name_th || undefined,
      account_name_en: formData.account_name_en || undefined,
      verification_type: formData.verification_type,
      is_active: formData.is_active,
      purpose: formData.purpose || undefined,
      notes: formData.notes || undefined,
    };

    switch (formData.account_source) {
      case 'bank':
        return {
          ...baseData,
          account_source: 'bank' as const,
          bank_code: formData.bank_code,
          account_number: formData.account_number,
          // Use Thai name as primary account name, or account number if not provided
          account_name: formData.account_name_th || formData.account_number,
        };

      case 'promptpay':
        return {
          ...baseData,
          account_source: 'promptpay' as const,
          promptpay_type: formData.promptpay_type,
          promptpay_id: formData.promptpay_id,
          qr_code_url: formData.qr_code_url || undefined,
        };

      case 'shop':
        return {
          ...baseData,
          account_source: 'shop' as const,
          shop_type: formData.shop_type,
          shop_code: formData.shop_code,
          shop_name: formData.shop_name,
        };

      case 'truemoney':
        return {
          ...baseData,
          account_source: 'truemoney' as const,
          truemoney_id: formData.truemoney_id,
        };

      default:
        // Fallback to bank account
        return {
          ...baseData,
          account_source: 'bank' as const,
          bank_code: formData.bank_code,
          account_number: formData.account_number,
        };
    }
  };

  /**
   * Submit form (create or update)
   */
  const handleSubmit = async () => {
    console.log('Form data before validation:', formData);
    
    if (!validateForm()) {
      console.log('Validation failed:', errors);
      return;
    }

    try {
      setIsLoading(true);
      const requestData = buildRequestData();
      
      console.log('Request data to send:', requestData);
      console.log('Branch ID:', branchId);

      if (accountId) {
        // Update existing account
        await accountAPI.update(accountId, requestData);
      } else {
        // Create new account
        const result = await accountAPI.create(branchId, requestData);
        console.log('Create result:', result);
      }

      onSuccess?.();
      reset();
    } catch (error: any) {
      console.error('Failed to save account:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle validation errors from backend
      if (error.response?.data?.details) {
        const backendErrors: AccountFormErrors = {};
        error.response.data.details.forEach((detail: any) => {
          backendErrors[detail.field] = detail.message;
        });
        setErrors(backendErrors);
      }
      
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset form to initial state
   */
  const reset = () => {
    setFormData(initialFormState);
    setErrors({});
  };

  return {
    formData,
    errors,
    config,
    branches,
    isLoading,
    isLoadingConfig,
    isLoadingBranches,
    updateField,
    handleSubmit,
    reset,
  };
}
